// Small helpers for "Connect an AI app": the request handle that rides in the address, the one
// place staff may be sent back to after signing in, and plain-words names for what an app can see.

const HANDLE_KEY = "gp_connect_request";
/** How long a kept handle is worth using. The API expires the request itself; this just stops an old one from lingering on a shared computer. */
export const HANDLE_KEEP_MS = 15 * 60 * 1000;

/**
 * The request handle comes in the address after the # (so it never reaches a server log or a
 * Referer header). It is taken out of the address bar straight away so it doesn't linger in
 * screenshots or history, and held in memory by the page.
 *
 * A handle in the address is NEVER stored. Storage is for exactly one trip: a person who is on
 * this page and follows the link to the staff sign-in (or the two-step page) saves the handle
 * first (keepRequestHandle), because the sign-in's emailed link opens in a NEW tab that has no
 * memory of this one. When /connect then opens with nothing in the address, it resumes a kept
 * handle only if it was saved that way within the last 15 minutes, and clears it at once, so
 * it is used one time. Otherwise any page that could open /connect#request=... (another
 * site's pop-up, the previous user of a shared computer) could plant a request that a later,
 * unrelated visit would silently pick up and show the person as if it were theirs.
 */
export function takeRequest(now: number = Date.now()): { handle: string; resumed: boolean } | null {
  let fromAddress: string | null = null;
  try {
    fromAddress = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("request");
  } catch {
    fromAddress = null;
  }
  // A handle in the address wins, and anything kept from earlier is dropped with it.
  if (fromAddress) {
    forgetRequestHandle();
    return { handle: fromAddress, resumed: false };
  }
  try {
    const raw = localStorage.getItem(HANDLE_KEY);
    if (raw === null) return null;
    // Used once, whatever it turns out to be.
    forgetRequestHandle();
    const kept: unknown = JSON.parse(raw);
    const { handle, savedAt } = (kept ?? {}) as { handle?: unknown; savedAt?: unknown };
    // A clock moved backwards (savedAt in the future) is treated as stale too.
    if (typeof handle === "string" && handle && typeof savedAt === "number" && savedAt <= now && now - savedAt <= HANDLE_KEEP_MS) return { handle, resumed: true };
    return null;
  } catch {
    // Unreadable or blocked: nothing kept.
    forgetRequestHandle();
    return null;
  }
}

export const takeRequestHandle = (now?: number): string | null => takeRequest(now)?.handle ?? null;

/**
 * Saves the handle for the one trip described above: call it when the person follows the link
 * to the staff sign-in or the two-step page, and nowhere else. Storage can be blocked (private
 * mode); then the trip simply ends at "Nothing to connect" and they start from the app again.
 */
export function keepRequestHandle(handle: string, now: number = Date.now()): void {
  try {
    localStorage.setItem(HANDLE_KEY, JSON.stringify({ handle, savedAt: now }));
  } catch {
    // Blocked: nothing kept.
  }
}

/** Removes the handle from the address bar (and nothing else about the address). */
export function hideRequestHandle(): void {
  if (window.location.hash) history.replaceState(null, "", window.location.pathname + window.location.search);
}

export function forgetRequestHandle(): void {
  try {
    localStorage.removeItem(HANDLE_KEY);
  } catch {
    // Nothing kept.
  }
}

/**
 * Where the admin may send staff after signing in or passing two-factor: the consent page and
 * nothing else. Anything that isn't plainly "/connect" on this site (another host, a protocol-
 * relative or backslash trick, an encoded slash, a longer path) is refused, so a crafted sign-in
 * link can't be turned into an open redirect. Returns the fixed path, never the input.
 */
export function connectReturnPath(next: string | null | undefined, origin: string = window.location.origin): string | null {
  if (!next || next.length > 200 || /[\\\u0000-\u001f]/.test(next)) return null;
  let url: URL;
  try {
    url = new URL(next, origin);
  } catch {
    return null;
  }
  return url.origin === origin && url.pathname === "/connect" ? "/connect" : null;
}

/** Only web addresses and app links; never something the browser would run as a script. */
export function safeRedirect(target: string): boolean {
  try {
    const { protocol } = new URL(target);
    return !["javascript:", "data:", "vbscript:", "file:", "blob:", "about:"].includes(protocol);
  } catch {
    return false;
  }
}

/** An https image address, or null: anything else shows the initial instead. */
export function httpsLogo(uri: string | null | undefined): string | null {
  if (!uri) return null;
  try {
    return new URL(uri).protocol === "https:" ? uri : null;
  } catch {
    return null;
  }
}

const SCOPE_WORDS: Record<string, string> = {
  "results:own": "Your own results",
  "results:team": "Results of teams you lead",
  "results:all": "Every result",
  "stats:read": "Stats",
  "site:read": "Campaigns and funnel stats",
  "site:write": "Change funnels and pages",
  "sales:write": "Make discount codes and personal test links",
  offline_access: "Stays connected",
};

/** The same, said about someone else (the admin's lists): "their", never "your". */
const THEIR_SCOPE_WORDS: Record<string, string> = {
  ...SCOPE_WORDS,
  "results:own": "Their own results",
  "results:team": "Results of teams they lead",
};

/**
 * What a connection can see, in plain words, to the person it belongs to ("Your own results").
 * Staying connected isn't something it "sees", so it is left out unless asked for.
 */
export function scopeWords(scopes: readonly string[], opts: { stays?: boolean } = {}): string[] {
  return wordsFor(SCOPE_WORDS, scopes, opts.stays);
}

/** Same words about another person, for staff looking at someone else's connection. */
export function scopeWordsAbout(scopes: readonly string[], opts: { stays?: boolean } = {}): string[] {
  return wordsFor(THEIR_SCOPE_WORDS, scopes, opts.stays);
}

function wordsFor(table: Record<string, string>, scopes: readonly string[], stays = false): string[] {
  // Writing includes reading (the API stores both), so only the bigger one is named.
  const named = scopes.includes("site:write") ? scopes.filter((s) => s !== "site:read") : scopes;
  const words = named.filter((s) => stays || s !== "offline_access").map((s) => table[s] ?? s);
  return [...new Set(words)];
}

/** Joins labels as a sentence-case list: "Their own results, results of teams they lead". */
export function sentenceList(words: readonly string[]): string {
  return words.map((w, i) => (i === 0 ? w : w.charAt(0).toLowerCase() + w.slice(1))).join(", ");
}

export const shortDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
